export type ChatMessageOpenApiScope = {
  catalogId?: string;
  catalogName: string;
  tag?: string;
};

export type ChatMessageAdaptorScope = {
  id: string;
  name?: string;
  type?: string;
};

/** Scope chips shown above a user bubble (not part of the visible message text). */
export type ChatMessageScope = {
  indices?: string[];
  /** Optional display names for index ids (guide title / friendly suffix). */
  indexLabels?: Record<string, string>;
  openApi?: ChatMessageOpenApiScope | null;
  adaptors?: ChatMessageAdaptorScope[];
};

/** Matches `OpenAPI katalog: Name. catalogId=uuid. Tag: X.` (and catalog / MBLFN variants). */
const OPENAPI_PREFIX_RE =
  /^(?:OpenAPI\s+(?:katalog|catalog)\s*:\s*|MBLFN\s+alan[ıi]\s*:\s*)(.+?)\.(?:\s*catalogId\s*=\s*([0-9a-f-]{36})\.)?(?:\s*Tag\s*:\s*([^.]+)\.)?\s*/i;
const ADAPTOR_PREFIX_RE = /^(?:Data\s+)?Adaptor\s*:\s*([0-9a-f-]{36})\s*/i;

export function stripChatMessagePrefixes(raw: string): {
  displayText: string;
  scope: ChatMessageScope;
} {
  let rest = (raw || "").trim();
  const adaptors: ChatMessageAdaptorScope[] = [];
  let openApi: ChatMessageOpenApiScope | null = null;

  // Peel leading adaptor / OpenAPI prefixes (order may vary).
  for (let i = 0; i < 12 && rest; i++) {
    const adaptorMatch = rest.match(ADAPTOR_PREFIX_RE);
    if (adaptorMatch) {
      const id = adaptorMatch[1];
      if (!adaptors.some((a) => a.id === id)) {
        adaptors.push({ id, name: id });
      }
      rest = rest.slice(adaptorMatch[0].length).trim();
      continue;
    }
    const openApiMatch = rest.match(OPENAPI_PREFIX_RE);
    if (openApiMatch) {
      const catalogName = (openApiMatch[1] || "").trim().replace(/\.$/, "");
      openApi = {
        catalogName: catalogName || openApiMatch[2] || "OpenAPI",
        catalogId: openApiMatch[2] || undefined,
        tag: openApiMatch[3]?.trim() || undefined,
      };
      rest = rest.slice(openApiMatch[0].length).trim();
      continue;
    }
    break;
  }

  return {
    displayText: rest,
    scope: {
      ...(openApi ? { openApi } : {}),
      ...(adaptors.length ? { adaptors } : {}),
    },
  };
}

function hasOwn<T extends object>(obj: T, key: keyof T): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function mergeChatMessageScope(
  base: ChatMessageScope | undefined,
  patch: ChatMessageScope | undefined
): ChatMessageScope | undefined {
  if (!base && !patch) return undefined;

  // When patch provides a family key, it replaces (including empty/null clears).
  const indices = patch && hasOwn(patch, "indices")
    ? Array.from(new Set((patch.indices || []).filter(Boolean)))
    : Array.from(new Set([...(base?.indices || []), ...(patch?.indices || [])].filter(Boolean)));

  let adaptors: ChatMessageAdaptorScope[];
  if (patch && hasOwn(patch, "adaptors")) {
    const byId = new Map<string, ChatMessageAdaptorScope>();
    for (const a of patch.adaptors || []) {
      if (!a?.id) continue;
      byId.set(a.id, { ...a });
    }
    adaptors = Array.from(byId.values());
  } else {
    const byId = new Map<string, ChatMessageAdaptorScope>();
    for (const a of [...(base?.adaptors || []), ...(patch?.adaptors || [])]) {
      if (!a?.id) continue;
      byId.set(a.id, { ...byId.get(a.id), ...a });
    }
    adaptors = Array.from(byId.values());
  }

  const openApi =
    patch && hasOwn(patch, "openApi") ? patch.openApi : (base?.openApi ?? null);

  const indexLabels = {
    ...(base?.indexLabels || {}),
    ...(patch?.indexLabels || {}),
  };

  const merged: ChatMessageScope = {
    ...(indices.length ? { indices } : {}),
    ...(Object.keys(indexLabels).length ? { indexLabels } : {}),
    ...(openApi ? { openApi } : {}),
    ...(adaptors.length ? { adaptors } : {}),
  };
  if (!merged.indices?.length && !merged.openApi && !merged.adaptors?.length) {
    return undefined;
  }
  return merged;
}

export function hasChatMessageScope(scope: ChatMessageScope | undefined): boolean {
  return Boolean(
    scope &&
      ((scope.indices && scope.indices.length > 0) ||
        scope.openApi ||
        (scope.adaptors && scope.adaptors.length > 0))
  );
}
